import React, { useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  Tabs,
  Tab
} from "@mui/material";
import { AiOutlineClose } from "react-icons/ai";
import DataTable from "../DataTable/DataTable";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{
        flex: value === index ? 1 : 0,
        display: value === index ? "flex" : "none",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {value === index && (
        <Box sx={{ p: 1, flex: 1, overflowY: "auto" }}>{children}</Box>
      )}
    </div>
  );
}

const ContractAmountBreakdownModal = ({ open, onClose, breakdownData }) => {
  const [breakdownTabValue, setBreakdownTabValue] = useState(0);

  const handleBreakdownTabChange = (event, newValue) => {
    setBreakdownTabValue(newValue);
  };

  const handleBreakdownCSV = () => {
    const csvRows = [];

    if (breakdownTabValue === 0 && Array.isArray(breakdownData?.sof) && breakdownData.sof.length > 0) {
      // SOF Details
      const headers = [
        "Contract ID", "Service ID", "Service Name", "Service Description",
        "SOF Doc ID", "SOF Doc Date", "SOF Currency",
        "MRC (USD)", "NRC (USD)", "MRC (Local)", "NRC (Local)",
        "NRC Installment Months", "Ref ID"
      ];
      csvRows.push(headers.join(","));
      breakdownData.sof.forEach((row) => {
        const values = [
          row.contract_id, row.service_id,
          row.service_name || row.service_name_x,
          row.service_description || row.service_description_doc,
          row.sof_doc_id, row.sof_doc_date, row.sof_currency,
          row.mrc, row.nrc, row.mrc_local, row.nrc_local,
          row.nrc_installment_months, row.ref_id
        ].map((val) => {
          const clean = String(val ?? "").replace(/"/g, '""').replace(/[\r\n]+/g, " ");
          return `"${clean}"`;
        });
        csvRows.push(values.join(","));
      });
    } else if (breakdownTabValue === 1 && Array.isArray(breakdownData?.amendment) && breakdownData.amendment.length > 0) {
      // Amendment Details
      const headers = ["Description", "Rate"];
      csvRows.push(headers.join(","));
      breakdownData.amendment.forEach((row) => {
        const values = [
          row.service_description_doc || row.service_description,
          row.rate
        ].map((val) => {
          const clean = String(val ?? "").replace(/"/g, '""').replace(/[\r\n]+/g, " ");
          return `"${clean}"`;
        });
        csvRows.push(values.join(","));
      });
    }

    if (csvRows.length === 0) return;

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    let tabSuffix = breakdownTabValue === 0 ? "SOF_Details" : "Amendment_Details";
    link.setAttribute("href", url);
    link.setAttribute("download", `Breakdown_${tabSuffix}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <div style={{ padding: "16px 24px", fontFamily: 'Arial, sans-serif', position: 'relative' }}>
        <AiOutlineClose
          size={24}
          style={{ position: 'absolute', top: 16, right: 16, cursor: 'pointer', color: '#111827' }}
          onClick={onClose}
        />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, color: '#1f2937', margin: 0 }}>
            Contract Amount Breakdown
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleBreakdownCSV}
              sx={{
                backgroundColor: '#4CAF50',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '13px',
                '&:hover': { backgroundColor: '#43A047' },
                mr: 4,
              }}
            >
              Download CSV
            </Button>
          </div>
        </div>

        {breakdownData?.contract_calculation
          && (breakdownData.contract_calculation.steps || []).length > 1 && (
          <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#F8F5FC', borderRadius: 1, border: '1px solid #E6DCF0' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
              How the Contract Amount was calculated
            </Typography>
            {(breakdownData.contract_calculation.steps || []).map((step, si, arr) => (
              <Box key={si} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Typography sx={{ fontSize: 13, color: '#374151', fontWeight: si === arr.length - 1 ? 600 : 400 }}>
                  {step.label}
                </Typography>
                <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap', fontWeight: si === arr.length - 1 ? 600 : 400 }}>
                  {Number(step.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            ))}
            {(breakdownData.contract_calculation.steps || []).filter(st => st.note).map((st, ni) => (
              <Typography key={`note${ni}`} sx={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>
                {st.note}
              </Typography>
            ))}
            {breakdownData.discrepancy_explanation && (
              <Typography sx={{ fontSize: 12, color: '#4b5563', mt: 1, pt: 1, borderTop: '1px dashed #D6C7E6' }}>
                {breakdownData.discrepancy_explanation}
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={breakdownTabValue}
            onChange={handleBreakdownTabChange}
            aria-label="contract amount breakdown tabs"
          >
            <Tab label={`SOF Details (${breakdownData?.sof?.length || 0})`} />
            <Tab label={`Amendment Details (${breakdownData?.amendment?.length || 0})`} />
          </Tabs>
        </Box>

        <TabPanel value={breakdownTabValue} index={0}>
          {Array.isArray(breakdownData?.sof) && breakdownData.sof.length > 0 ? (
            <DataTable 
              columns={[
                { field: "contract_id", headerName: "Contract ID", width: 120 },
                { field: "service_id", headerName: "Service ID", width: 130 },
                { field: "service_name", headerName: "Service Name", width: 200 },
                { field: "service_description", headerName: "Service Description", width: 300 },
                { field: "sof_doc_id", headerName: "SOF Doc ID", width: 130 },
                { field: "sof_doc_date", headerName: "SOF Doc Date", width: 150 },
                { field: "sof_currency", headerName: "SOF Currency", width: 120 },
                { field: "mrc", headerName: "MRC (USD)", width: 130 },
                { field: "nrc", headerName: "NRC (USD)", width: 130 },
                { field: "mrc_local", headerName: "MRC (Local)", width: 130 },
                { field: "nrc_local", headerName: "NRC (Local)", width: 130 },
                { field: "nrc_installment_months", headerName: "NRC Installment Months", width: 180 },
                { field: "ref_id", headerName: "Ref ID", width: 100 },
              ]}
              rows={breakdownData.sof.map((row, index) => ({
                ...row,
                id: index + 1,
                contract_id: row.contract_id || "N/A",
                service_id: row.service_id || "N/A",
                service_name: row.service_name || row.service_name_x || "N/A",
                service_description: row.service_description || row.service_description_doc || "N/A",
                sof_doc_id: row.sof_doc_id || "N/A",
                sof_doc_date: row.sof_doc_date || "N/A",
                sof_currency: row.sof_currency || "N/A",
                mrc: row.mrc != null ? Number(row.mrc).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00",
                nrc: row.nrc != null ? Number(row.nrc).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00",
                mrc_local: row.mrc_local != null ? Number(row.mrc_local).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00",
                nrc_local: row.nrc_local != null ? Number(row.nrc_local).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00",
                nrc_installment_months: row.nrc_installment_months ?? "N/A",
                ref_id: row.ref_id || "N/A",
              }))}
              height={300}
            />
          ) : (
            <Typography sx={{ p: 2 }}>No SOF Details available.</Typography>
          )}
          {Array.isArray(breakdownData?.sof) && breakdownData.sof.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, mt: 1, pr: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                Total MRC (USD): {breakdownData.sof.reduce((t, r) => t + (Number(r.mrc) || 0), 0)
                  .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                Total NRC (USD): {breakdownData.sof.reduce((t, r) => t + (Number(r.nrc) || 0), 0)
                  .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={breakdownTabValue} index={1}>
          {Array.isArray(breakdownData?.amendment) && breakdownData.amendment.length > 0 ? (
            <DataTable 
              columns={[
                { field: "service_description_doc", headerName: "Description", width: 500 },
                { field: "rate", headerName: "Rate", width: 150 },
              ]}
              rows={breakdownData.amendment.map((row, index) => ({
                ...row,
                id: index + 1,
                service_description_doc: row.service_description_doc || row.service_description || "N/A",
              }))}
              height={300}
            />
          ) : (
            <Typography sx={{ p: 2 }}>No Amendment Details available.</Typography>
          )}
        </TabPanel>
      </div>
    </Dialog>
  );
};

export default ContractAmountBreakdownModal;
